/**
 * Glyph — deterministic SVG sigil generated from an Orchist's glyph_seed.
 *
 * Same seed → byte-identical SVG. Different seeds → visibly different
 * sigils. The glyph is the visual prayer; for Generic-mode Orchists it
 * IS the identity (no name shown). For Named/Aliased Orchists it is the
 * personal seal that travels with their record forever.
 *
 * Design constraints (per sacred/lineage.md):
 *   - Looks like a sigil/seal, not a barcode or QR
 *   - Always composed: center, rings, spokes, star
 *   - Uses the Manifesto palette (ink + accent) — no extra colors
 *   - No external dependencies (pure JS + inline SVG)
 *
 * Algorithm:
 *   1. FNV-1a 32-bit hash of the seed → reproducible bit pattern
 *   2. Iterated re-hashing produces enough bytes for all parameters
 *   3. Bytes mapped to enums of pleasing values (no ugly outliers)
 *   4. Composed as nested rings + radial spokes + central star + dot
 */

import { useMemo } from 'react';

// ---------------------------------------------------------------------------
// Hash & param extraction
// ---------------------------------------------------------------------------

/** FNV-1a 32-bit hash. Deterministic, fast, no dependencies. */
function fnv1a(s: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    hash ^= s.charCodeAt(i);
    hash = (hash * 0x01000193) >>> 0;
  }
  return hash;
}

/** Generate `count` pseudo-random bytes deterministically from a seed. */
function seededBytes(seed: string, count: number): number[] {
  const bytes: number[] = [];
  let h = fnv1a(seed);
  while (bytes.length < count) {
    h = fnv1a(`${seed}:${bytes.length}:${h.toString(16)}`);
    bytes.push(h & 0xff);
    bytes.push((h >>> 8) & 0xff);
    bytes.push((h >>> 16) & 0xff);
    bytes.push((h >>> 24) & 0xff);
  }
  return bytes.slice(0, count);
}

interface GlyphParams {
  outerRings: number;     // 1-2 outer concentric rings
  innerRing: boolean;     // optional inner ring near center
  spokes: number;         // count of radial spokes (6/8/12 — tasteful)
  starPoints: number;     // count of star points (5/6/8)
  rotation: number;       // 0-60deg subtle off-axis
  starSize: number;       // 0.18-0.26 of viewBox half-width
  centerDot: number;      // 1.5-3 radius
  spokeBoldIndex: number; // index of one accented spoke for asymmetry
}

function paramsFromSeed(seed: string): GlyphParams {
  const b = seededBytes(seed, 12);
  const SPOKE_OPTIONS = [6, 8, 12];
  const STAR_OPTIONS = [5, 6, 8];

  const spokes = SPOKE_OPTIONS[b[2] % SPOKE_OPTIONS.length];

  return {
    outerRings: 1 + (b[0] % 2),
    innerRing: (b[1] & 0x01) === 1,
    spokes,
    starPoints: STAR_OPTIONS[b[3] % STAR_OPTIONS.length],
    rotation: (b[4] / 255) * 60,
    starSize: 0.18 + (b[5] / 255) * 0.08,
    centerDot: 1.5 + (b[6] / 255) * 1.5,
    spokeBoldIndex: b[7] % spokes,
  };
}

// ---------------------------------------------------------------------------
// React component
// ---------------------------------------------------------------------------

export interface GlyphProps {
  /** The Orchist's glyph_seed string. Stable forever once assigned. */
  seed: string;
  /** Render size in px. Defaults to 80. */
  size?: number;
  /** Optional CSS class on the root <svg>. */
  className?: string;
  /** Optional aria-label override. Defaults to "Orchist sigil". */
  label?: string;
}

const VIEW = 100;
const CENTER = VIEW / 2;
const MAX_R = 46;

export function Glyph({ seed, size = 80, className, label = 'Orchist sigil' }: GlyphProps) {
  const params = useMemo(() => paramsFromSeed(seed), [seed]);

  // Outer rings — concentric, fading toward center
  const outerRings = Array.from({ length: params.outerRings }).map((_, i) => {
    const r = MAX_R - i * 4;
    return (
      <circle
        key={`outer-${i}`}
        cx={CENTER}
        cy={CENTER}
        r={r}
        fill="none"
        stroke="currentColor"
        strokeWidth={i === 0 ? 0.6 : 0.4}
        opacity={0.32 - i * 0.08}
      />
    );
  });

  const innerRingEl = params.innerRing ? (
    <circle
      cx={CENTER}
      cy={CENTER}
      r={MAX_R * 0.55}
      fill="none"
      stroke="currentColor"
      strokeWidth={0.35}
      opacity={0.22}
      strokeDasharray="1.5 2"
    />
  ) : null;

  // Radial spokes — light geometry connecting outer to inner
  const baseAngleOffset = (params.rotation * Math.PI) / 180;
  const spokes = Array.from({ length: params.spokes }).map((_, i) => {
    const angle = (i / params.spokes) * 2 * Math.PI + baseAngleOffset;
    const innerR = MAX_R * 0.4;
    const outerR = MAX_R * 0.92;
    const x1 = CENTER + Math.cos(angle) * innerR;
    const y1 = CENTER + Math.sin(angle) * innerR;
    const x2 = CENTER + Math.cos(angle) * outerR;
    const y2 = CENTER + Math.sin(angle) * outerR;
    const isBold = i === params.spokeBoldIndex;
    return (
      <line
        key={`spoke-${i}`}
        x1={x1}
        y1={y1}
        x2={x2}
        y2={y2}
        stroke="currentColor"
        strokeWidth={isBold ? 0.7 : 0.35}
        opacity={isBold ? 0.55 : 0.32}
        strokeLinecap="round"
      />
    );
  });

  // Central star — the visual focus
  const starOuter = MAX_R * params.starSize;
  const starInner = starOuter * 0.42;
  const starVerts = Array.from({ length: params.starPoints * 2 }).map((_, i) => {
    const angle = (i / (params.starPoints * 2)) * 2 * Math.PI - Math.PI / 2 + baseAngleOffset;
    const r = i % 2 === 0 ? starOuter : starInner;
    return `${(CENTER + Math.cos(angle) * r).toFixed(2)},${(CENTER + Math.sin(angle) * r).toFixed(2)}`;
  });

  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${VIEW} ${VIEW}`}
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      role="img"
      aria-label={label}
    >
      <title>{label}</title>

      {outerRings}
      {innerRingEl}
      {spokes}

      {/* Star — slightly accented */}
      <polygon
        points={starVerts.join(' ')}
        fill="currentColor"
        stroke="none"
        opacity={0.7}
      />

      {/* Center dot — accent color, the still point */}
      <circle
        cx={CENTER}
        cy={CENTER}
        r={params.centerDot}
        fill="var(--orchist-accent, #c9a961)"
      />
    </svg>
  );
}

/**
 * Convenience: render the public-facing string a Generic-mode Orchist sees,
 * in the form `Orchist · YYYY-MM-DD`. The accompanying <Glyph> distinguishes them.
 *
 * Pure helper — keeps the rendering site clean.
 */
export function genericLineageLabel(invitationAcceptedAt: string | null | undefined): string {
  if (!invitationAcceptedAt) return 'Orchist';
  const date = invitationAcceptedAt.slice(0, 10); // YYYY-MM-DD
  return `Orchist · ${date}`;
}
